import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ChevronLeft } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PrivacyPolicyPage() {
  const [privacy, setPrivacy] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/config/privacy`).then(r => setPrivacy(r.data)).catch(() => {});
  }, []);

  const name = privacy?.privacy_responsible_name || "Ascauto, S.A.";
  const cif = privacy?.privacy_responsible_cif || "A79668307";
  const email = privacy?.privacy_contact_email || "RGPD@ascauto.com";

  return (
    <div className="py-12 sm:py-16" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6 transition-colors">
          <ChevronLeft className="w-3 h-3" /> Volver al inicio
        </Link>

        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="heading-industrial text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase tracking-tight" data-testid="privacy-policy-title">
              Politica de Privacidad
            </h1>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">Proteccion de datos personales</p>
          </div>
        </div>

        {/* 1. Responsable */}
        <Section title="1. Responsable del tratamiento">
          <Row label="Identidad" value={name} />
          <Row label="CIF" value={cif} />
          <Row label="Domicilio social" value="C. de Simón Hernández, 71, 28937 Móstoles, Madrid, España" />
          <Row label="Actividad" value="Taller oficial y concesionario Stellantis (Fiat, Abarth, Alfa Romeo, Jeep, Opel, Leapmotor, Fiat Professional)" />
          <Row label="Email del Delegado de Proteccion de Datos" value={<a href={`mailto:${email}`} className="text-red-600 underline hover:text-red-700">{email}</a>} />
          <Row label="Sitio web" value={<a href="https://ascauto.com" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">ascauto.com</a>} />
        </Section>

        {/* 2. Tratamientos */}
        <Section title="2. Tratamientos de datos personales">
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            En {name} tratamos tus datos personales con las siguientes finalidades, bases juridicas y plazos de conservacion, en funcion de cada formulario o canal de recogida:
          </p>

          <Treatment
            id="seguimiento"
            title="Seguimiento de tu vehiculo / Habla con tu asesor"
            purpose="Verificar tu identidad mediante matrícula y teléfono móvil, localizar tu vehículo en nuestro sistema y conectarte con tu asesor de servicio asignado para informarte sobre el estado de la reparación o mantenimiento."
            data="Matrícula del vehículo, número de teléfono móvil."
            legal="Ejecucion del contrato de servicio de taller suscrito entre el cliente y Ascauto (art. 6.1.b RGPD)."
            recipients="Personal de posventa y asesores de servicio de Ascauto."
            retention="Mientras dure la relacion comercial y, posteriormente, durante los plazos legales de prescripcion aplicables (5 anos con caracter general)."
          />
          <Treatment
            id="cita"
            title="Reservar cita en taller"
            purpose="Gestionar tu solicitud de cita previa, verificar la disponibilidad de nuestros centros de Móstoles y Alcorcón, y contactarte por teléfono o email para confirmar la fecha y hora definitivas."
            data="Nombre, apellidos, teléfono, email (opcional), matrícula, marca, modelo, kilometraje, centro preferido, fecha y hora solicitadas, tipo de servicio y descripción de la avería o necesidad."
            legal="Ejecucion de contrato o medidas precontractuales (art. 6.1.b RGPD) y consentimiento del interesado (art. 6.1.a RGPD)."
            recipients="Personal de recepcion, jefe de taller y asesor de servicio asignado."
            retention="Mientras dure la relacion comercial y durante los plazos legales de prescripcion."
          />
          <Treatment
            id="atencion"
            title="Solicitar atencion / Contacto general"
            purpose="Atender tu solicitud de contacto, resolver dudas, facilitar presupuestos o gestionar cualquier consulta de posventa cuando no ha sido posible identificar automaticamente tu vehiculo en nuestro sistema."
            data="Nombre, teléfono, email (opcional), matrícula (opcional) y mensaje descriptivo."
            legal="Consentimiento del interesado (art. 6.1.a RGPD)."
            recipients="Departamento de atencion al cliente y, en su caso, departamento tecnico."
            retention="Durante el tiempo necesario para tramitar la solicitud y, en su caso, durante los plazos legales de prescripcion."
          />
          <Treatment
            id="whatsapp"
            title="WhatsApp y comunicaciones directas"
            purpose="Atender consultas, enviar información sobre el estado de reparaciones y facilitar la comunicación entre el cliente y su asesor de servicio a través de WhatsApp u otros canales de mensajería."
            data="Número de teléfono, nombre, contenido de la conversación y datos facilitados voluntariamente."
            legal="Consentimiento del interesado (art. 6.1.a RGPD) y ejecucion de contrato (art. 6.1.b RGPD)."
            recipients="Asesor de servicio asignado y, en su caso, personal de recepcion."
            retention="Mientras dure la relacion comercial y durante los plazos legales de prescripcion."
          />
          <Treatment
            id="comercial"
            title="Comunicaciones comerciales"
            purpose="Enviarte información sobre ofertas, promociones, novedades de producto, campañas de mantenimiento y servicios de Ascauto, siempre que hayas dado tu consentimiento expreso marcando la casilla correspondiente."
            data="Nombre, teléfono, email."
            legal="Consentimiento expreso del interesado (art. 6.1.a RGPD). La casilla de comunicaciones comerciales es siempre opcional e independiente de la aceptacion de la politica de privacidad."
            recipients="Departamento de marketing de Ascauto. No se ceden datos a terceros para fines comerciales."
            retention="Hasta que revoques tu consentimiento. Puedes darte de baja en cualquier momento enviando un email a {email} o utilizando el enlace de baja incluido en cada comunicacion."
          />
        </Section>

        {/* 3. Destinatarios */}
        <Section title="3. Destinatarios de los datos">
          <p className="text-sm text-slate-600 leading-relaxed mb-2">
            Tus datos personales no se cederan a terceros salvo obligacion legal. Podran ser tratados por los siguientes encargados del tratamiento, que nos prestan servicios bajo contrato:
          </p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
            <li>Proveedores de infraestructura tecnologica y alojamiento web.</li>
            <li>Proveedores de servicios de correo electronico transaccional.</li>
            <li>Stellantis y sus marcas, en el marco de las obligaciones contractuales de la red oficial.</li>
          </ul>
        </Section>

        {/* 4. Transferencias */}
        <Section title="4. Transferencias internacionales">
          <p className="text-sm text-slate-600 leading-relaxed">
            Con caracter general, no se realizan transferencias de datos personales fuera del Espacio Economico Europeo. En caso de que algun proveedor tecnologico almacene datos fuera del EEE, se garantiza la existencia de garantias adecuadas (Clausulas Contractuales Tipo de la Comision Europea o decision de adecuacion).
          </p>
        </Section>

        {/* 5. Derechos */}
        <Section title="5. Tus derechos">
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            La normativa de proteccion de datos te reconoce los siguientes derechos:
          </p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1.5 ml-2">
            <li><strong>Acceso:</strong> conocer que datos personales tuyos estamos tratando.</li>
            <li><strong>Rectificacion:</strong> solicitar la correccion de datos inexactos o incompletos.</li>
            <li><strong>Supresion:</strong> solicitar la eliminacion de tus datos cuando ya no sean necesarios.</li>
            <li><strong>Limitacion:</strong> solicitar la restriccion del tratamiento en determinadas circunstancias.</li>
            <li><strong>Portabilidad:</strong> recibir tus datos en un formato estructurado y de uso comun.</li>
            <li><strong>Oposicion:</strong> oponerte al tratamiento de tus datos, incluida la elaboracion de perfiles.</li>
            <li><strong>Revocacion del consentimiento:</strong> retirar tu consentimiento en cualquier momento, sin que ello afecte a la licitud del tratamiento previo.</li>
          </ul>
          <p className="text-sm text-slate-600 leading-relaxed mt-3">
            Para ejercer cualquiera de estos derechos, dirígete a <a href={`mailto:${email}`} className="text-red-600 underline hover:text-red-700 font-medium">{email}</a> o a nuestra dirección postal, acompañando copia de tu DNI o documento acreditativo de identidad.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mt-2">
            Asimismo, tienes derecho a presentar una reclamacion ante la Agencia Espanola de Proteccion de Datos (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">www.aepd.es</a>), especialmente si consideras que no hemos atendido satisfactoriamente el ejercicio de tus derechos.
          </p>
        </Section>

        {/* 6. Seguridad */}
        <Section title="6. Medidas de seguridad">
          <p className="text-sm text-slate-600 leading-relaxed">
            {name} aplica medidas tecnicas y organizativas apropiadas para garantizar un nivel de seguridad adecuado al riesgo, incluyendo cifrado de comunicaciones, controles de acceso basados en roles, copias de seguridad periodicas y procedimientos de gestion de incidentes, conforme al articulo 32 del RGPD.
          </p>
        </Section>

        {/* 7. Cookies */}
        <Section title="7. Cookies">
          <p className="text-sm text-slate-600 leading-relaxed">
            Este sitio web utiliza cookies propias y de terceros. Para más información, consulta nuestra{" "}
            <Link to="/politica-de-cookies" className="text-red-600 underline hover:text-red-700 font-medium">Politica de cookies</Link>.
          </p>
        </Section>

        {/* 8. Actualizacion */}
        <Section title="8. Actualizacion de esta politica">
          <p className="text-sm text-slate-600 leading-relaxed">
            {name} se reserva el derecho a modificar la presente Politica de Privacidad para adaptarla a novedades legislativas, jurisprudenciales o de practica empresarial. En caso de cambios sustanciales, se informara a los usuarios a traves de este sitio web.
          </p>
        </Section>

        <p className="text-xs text-slate-400 text-center mt-10 pt-6 border-t border-slate-100">Ultima actualizacion: febrero 2026 | {name} | CIF {cif}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 pb-2 border-b border-slate-200">{title}</h2>
      <div>{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2 py-1.5 text-sm border-b border-slate-50 last:border-0">
      <span className="font-semibold text-slate-800 sm:w-64 flex-shrink-0">{label}:</span>
      <span className="text-slate-600">{value}</span>
    </div>
  );
}

function Treatment({ id, title, purpose, data, legal, recipients, retention }) {
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden mb-3" data-testid={`treatment-${id}`}>
      <div className="bg-slate-50 px-5 py-3 border-b border-slate-200">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      </div>
      <div className="p-5 space-y-2.5 text-sm text-slate-600">
        <TRow label="Finalidad" value={purpose} />
        <TRow label="Datos tratados" value={data} />
        <TRow label="Base juridica" value={legal} />
        <TRow label="Destinatarios" value={recipients} />
        <TRow label="Conservacion" value={retention} />
      </div>
    </div>
  );
}

function TRow({ label, value }) {
  return (
    <div>
      <span className="font-semibold text-slate-800 text-xs uppercase tracking-wider">{label}: </span>
      <span className="leading-relaxed">{value}</span>
    </div>
  );
}
